import React, { useEffect, useState } from 'react'
import styles from '../registration.module.scss'
import i18n from 'constants/strings'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { getFastTrackPersonDetails } from 'actions/fastTrackReg-actions'
import UserMessageScreen from 'components/generic/UserMessageScreen'
import { MESSAGE_SCREENS, MessageTypes } from 'constants/message-screens'
import { getOwnProfileUrl, getQueryParam, getSignInUrl, isOfAge } from 'utils'
import { ReactComponent as IconOne } from 'images/icons/icn_ft_activity.svg'
import { ReactComponent as IconTwo } from 'images/icons/icn_ft_gamezone.svg'
import { ReactComponent as IconThree } from 'images/icons/icn_ft_history.svg'
import InfoBox from 'components/generic/InfoBox'
import { Formik, Form } from 'formik'
import BtnPrimary from 'components/generic/BtnPrimary'
import TermsAndPolicy, { TermsAndPolicyType } from '../common/TermsAndPolicy'
import LoadingSpinner from '../common/LoadingSpinner'
import FormCheckbox from 'components/generic/FormCheckbox'
import { FormikNames } from 'types/forms/formik-names'
import { Trans } from 'react-i18next'
import { CONTACT_US_HREF, RegistrationData } from 'constants/global'
import ExistingItfCustomerText from '../common/ExistingItfCustomerText'
import ResponsiveText from '../../generic/ResponsiveText'
import ErrorScreen from 'components/generic/ErrorScreen'
import Storage from 'modules/storage'
import { startRegistrationFlow, startRegistrationFlowExistingCustomer } from '../utils'
import { createRegLog } from 'modules/reg-logger'
import ParentalConsent from '../common/ParentalConsent'
import { FastTrackPersonDetails } from 'types/global'
import { BackArrow } from 'components/Icons'
import { useFastTrackRegValidationSchema } from 'forms/validation/schemas'
import { makeValidationFunc } from 'forms/validation/validators'

const AlreadyRegistered = () => {
  const { icon, header, backLinkText } = MESSAGE_SCREENS.ALREADY_REGISTERED
  const handleClick = () => {
    const toUrl = getSignInUrl(getOwnProfileUrl())
    createRegLog('redirectAlreadyRegistered', { to: toUrl })
    window.location.assign(toUrl)
  }

  useEffect(() => {
    createRegLog('alreadyRegistered')
  }, [])

  return (
    <UserMessageScreen
      icon={icon}
      header={header}
      backLink={
        <BtnPrimary
          onClick={handleClick}
          border={false}
          className={styles.alreadyRegisteredLoginBtn}
        >
          {backLinkText}
        </BtnPrimary>
      }
    />
  )
}

const RegistrationSteps = ({
  activeStep,
  personDetails,
}: {
  activeStep: number
  personDetails: FastTrackPersonDetails
}) => {
  const welcomeTitle = `${i18n.t('strFastTrackRegistration.welcome')} ${personDetails?.firstName} ${
    personDetails?.lastName
  }`

  if (activeStep === 0)
    return (
      <>
        <ResponsiveText as={'h1'} compareString={welcomeTitle} classLongText={styles.smallTitle}>
          {welcomeTitle}
        </ResponsiveText>

        <p className={styles.introText}>
          {personDetails.alreadyItfCustomer ? (
            <ExistingItfCustomerText />
          ) : (
            <Trans ns='strRegister' i18nKey={'intro'}>
              Create your ITF Tennis ID user account to gain access to the{' '}
              <strong>ITF World Tennis Number</strong>.
            </Trans>
          )}
        </p>

        <ul className={styles.list}>
          <li className={styles.listItem}>
            <IconOne className={styles.listItemIcon} />
            {i18n.t('strFastTrackRegistration.features.one')}
          </li>
          <li className={styles.listItem}>
            <IconTwo className={styles.listItemIcon} />
            {i18n.t('strFastTrackRegistration.features.two')}
          </li>
          <li className={styles.listItem}>
            <IconThree className={styles.listItemIcon} />
            {i18n.t('strFastTrackRegistration.features.three')}
          </li>
        </ul>

        <InfoBox className={styles.infoBox}>
          <ResponsiveText compareString={welcomeTitle} classLongText={styles.smallText}>
            <Trans
              ns='strRegister'
              i18nKey={'infoBoxOne'}
              values={{
                firstName: personDetails?.firstName,
                lastName: personDetails?.lastName,
              }}
              components={{
                1: <br />,
                2: (
                  <a href={CONTACT_US_HREF} target='_blank' rel='noopener noreferrer'>
                    Click here
                  </a>
                ),
              }}
            >
              {'Not {{firstName}} {{lastName}}?<1/><2>Click here</2> for what to do next'}
            </Trans>
          </ResponsiveText>
        </InfoBox>
        <div className={styles.consents}>
          <FormCheckbox
            name={FormikNames.COMM_CONSENT_ITF}
            label={i18n.t('strRegister.commConsentItf')}
          />
          <FormCheckbox
            name={FormikNames.COMM_CONSENT_NA}
            label={i18n.t('strRegister.commConsentNa')}
          />
        </div>
        <TermsAndPolicy type={TermsAndPolicyType.WELCOME} className={styles.termsText} />
      </>
    )

  if (activeStep === 1)
    return (
      <>
        <h1>{i18n.t('strRegister.parentalConsent.fastTrack.title')}</h1>
        <TermsAndPolicy type={TermsAndPolicyType.WELCOME_PARENT} className={styles.termsText} />
        <ParentalConsent className={styles.parentGuardianSectionFastTrack} />
      </>
    )

  return null
}

const WelcomeUser = () => {
  const regToken = getQueryParam('token')
  const dispatch = useDispatch()
  const [activeStep, setActiveStep] = useState(0)
  const personDetails = useSelector((state: AppState) => state.fastTrackReg.personDetails)
  const loading = useSelector((state: AppState) => state.fastTrackReg.loadingPersonDetails)
  const error = useSelector((state: AppState) => state.fastTrackReg.error)
  const fastTrackRegValidationSchema = useFastTrackRegValidationSchema()
  useEffect(() => {
    dispatch(getFastTrackPersonDetails(regToken))
  }, [dispatch, regToken])

  if (error)
    return (
      <ErrorScreen
        type={MessageTypes.FAST_TRACK_ERROR}
        message={error.message}
        backLink={CONTACT_US_HREF}
      />
    )

  if (loading || !personDetails) return <LoadingSpinner />

  if (personDetails?.alreadyRegisteredForService) return <AlreadyRegistered />

  const parentalConsentRequired = !personDetails.birthDate || !isOfAge(personDetails.birthDate)
  const lastStep = parentalConsentRequired ? 1 : 0
  const isLastStep = activeStep === lastStep
  const renderBackBtn = !!activeStep
  const handleValidate = parentalConsentRequired
    ? makeValidationFunc(
        [FormikNames.PARENT_GUARDIAN_NAME, FormikNames.PARENT_GUARDIAN_EMAIL],
        fastTrackRegValidationSchema
      )
    : undefined

  return (
    <>
      {renderBackBtn && (
        <BtnPrimary
          border={false}
          className={styles.backLink}
          onClick={() => setActiveStep(prev => prev - 1)}
        >
          <BackArrow aria-hidden />
          {i18n.t('strCommonUI.link.back')}
        </BtnPrimary>
      )}
      <Formik
        enableReinitialize
        initialValues={{
          [FormikNames.COMM_CONSENT_ITF]: false,
          [FormikNames.COMM_CONSENT_NA]: false,
          [FormikNames.PARENT_GUARDIAN_NAME]: '',
          [FormikNames.PARENT_GUARDIAN_EMAIL]: '',
        }}
        validate={handleValidate}
        onSubmit={({
          commConsentItf,
          commConsentNa,
          parentGuardianFullName,
          parentGuardianEmail,
        }) => {
          const regData: RegistrationData = {
            commConsentItf,
            commConsentNa,
            termsAndConditions: new Date().toISOString(),
            isWalkUpUser: false,
          }

          if (parentalConsentRequired) {
            regData['parentFullName'] = parentGuardianFullName
            regData['parentEmailAddress'] = parentGuardianEmail
          }

          Storage.saveRegistrationData(regData)

          if (personDetails.alreadyItfCustomer) {
            createRegLog('alreadyCustomerFT')
            startRegistrationFlowExistingCustomer()
          } else {
            startRegistrationFlow(regToken)
          }
        }}
      >
        {({ errors, values }) => (
          <Form>
            <RegistrationSteps activeStep={activeStep} personDetails={personDetails} />
            {isLastStep && (
              <BtnPrimary size={'medium'} type={'submit'} className={styles.submitBtn}>
                {i18n.t('strCommonUI.button.continue')}
              </BtnPrimary>
            )}
          </Form>
        )}
      </Formik>
      {!isLastStep && (
        <BtnPrimary
          size={'medium'}
          className={styles.submitBtn}
          onClick={() => setActiveStep(prev => prev + 1)}
        >
          {i18n.t('strCommonUI.button.continue')}
        </BtnPrimary>
      )}
    </>
  )
}

export default WelcomeUser
